import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../redux/slices/authSlice';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    phone: '',
    otp: ''
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [loginStep, setLoginStep] = useState(1); // 1 = initial, 2 = OTP verification
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isAccountSuspended, setIsAccountSuspended] = useState(false);
  const [lastOtp, setLastOtp] = useState(null); // Store the last OTP received

  // Debug environment variables
  console.log('Current Environment:', process.env.NODE_ENV);
  console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const validateForm = () => {
    const { phone, otp } = formData;

    if (loginStep === 1) {
      if (!phone) {
        return 'Phone number is required.';
      }

      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(phone)) {
        return 'Enter a valid 10-digit phone number.';
      }
    }

    if (loginStep === 2 && !otp) {
      return 'OTP is required.';
    }

    return '';
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const sendLoginOtp = async () => {
    try {
      setLoading(true);
      setMessage('');
      setIsAccountSuspended(false);
      setLastOtp(null); // Reset previous OTP

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
        { phone: formData.phone },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );

      console.log('OTP sent:', response);
      
      // Store the OTP from response for display
      if (response.data.otp) {
        setLastOtp(response.data.otp);
        toast.success('OTP sent to your phone! Also displayed below due to SMS service issues.');
      } else {
        toast.success('OTP sent to your phone!');
      }
      
      setLoginStep(2);
      setResendCooldown(120); // 2 minutes cooldown
    } catch (error) {
      console.error('OTP send error:', error);
      
      
      // Handle account suspension error
      if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
        setIsAccountSuspended(true);
        setMessage('Your account has been suspended. Please contact support.');
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }
      
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to send OTP. Please try again.';
      setMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0) return;
    
    try {
      setLoading(true);
      setMessage('');
      setIsAccountSuspended(false);
      setLastOtp(null); // Reset previous OTP

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
        { phone: formData.phone },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );

      console.log('OTP resent:', response);
      
      // Store the OTP from response for display
      if (response.data.otp) {
        setLastOtp(response.data.otp);
        toast.success('OTP resent to your phone! Also displayed below due to SMS service issues.');
      } else {
        toast.success('OTP resent to your phone!');
      }
      
      setResendCooldown(120); // Reset cooldown to 2 minutes
    } catch (error) {
      console.error('OTP resend error:', error);
      
      // Handle account suspension error
      if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
        setIsAccountSuspended(true);
        setMessage('Your account has been suspended. Please contact support.');
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }
      
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to resend OTP. Please try again.';
      setMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginOtp = async () => {
    try {
      setLoading(true);
      setMessage('');
      setIsAccountSuspended(false);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/verifyLoginOtpAndLogin`,
        { phone: formData.phone, otp: formData.otp },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );

      console.log('Login response:', response);

      dispatch(
        loginSuccess({
          user: response?.data?.user,
          token: response?.data?.token
        })
      );

      Cookies.set('ecom_user_token', response?.data?.token, {
        expires: 1,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'Lax'
      });

      toast.success('Login successful!');
      navigate('/home');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle account suspension error
      if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
        setIsAccountSuspended(true);
        setMessage('Your account has been suspended. Please contact support.');
        toast.error('Your account has been suspended. Please contact support.');
        return;
      }
      
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Login failed. Please try again.';
      setMessage(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }
    
    if (loginStep === 1) {
      await sendLoginOtp();
    } else {
      await verifyLoginOtp();
    }
  };

  const handleBack = () => {
    setLoginStep(1);
    setFormData(prev => ({ ...prev, otp: '' }));
    setMessage('');
    setIsAccountSuspended(false);
    setResendCooldown(0);
    setLastOtp(null);
  };

  // Format time for display (MM:SS)
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-fill OTP if available
  useEffect(() => {
    if (lastOtp && loginStep === 2) {
      setFormData(prev => ({ ...prev, otp: lastOtp.toString() }));
    }
  }, [lastOtp, loginStep]);

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            {loginStep === 1 ? 'Login with Phone Number' : 'Verify OTP'}
          </h2>

          {isAccountSuspended && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="font-bold">Account Suspended</p>
              <p>Your account has been suspended. Please contact support for assistance.</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {loginStep === 1 ? (
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter 10-digit phone number"
                    disabled={isAccountSuspended}
                  />
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <p className="text-gray-600">
                      Enter OTP sent to <span className="font-medium">******{formData.phone.slice(-3)}</span>
                    </p>
                    
                    {/* Display OTP due to SMS service issues */}
                    {lastOtp && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800 mb-1">
                          SMS service is having issues. Your OTP is:
                        </p>
                        <div className="text-center">
                          <span className="text-2xl font-bold text-yellow-700">{lastOtp}</span>
                        </div>
                        <p className="text-xs text-yellow-600 mt-1">
                          This OTP has been auto-filled for your convenience.
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                      OTP Code
                    </label>
                    <input
                      type="text"
                      name="otp"
                      id="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
                      placeholder="123456"
                      maxLength={6}
                      disabled={isAccountSuspended}
                    />
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="text-sm text-blue-600 font-medium hover:underline"
                    >
                      Back to login
                    </button>
                  </div>
                  
                  {/* Resend OTP section */}
                  <div className="text-center mt-4">
                    <p className="text-gray-600 text-sm">
                      Didn't receive the code?{' '}
                      {resendCooldown > 0 ? (
                        <span className="text-gray-500">
                          Resend in {formatTime(resendCooldown)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={resendOtp}
                          className="text-blue-600 font-medium hover:underline"
                          disabled={loading || isAccountSuspended}
                        >
                          Resend OTP
                        </button>
                      )}
                    </p>
                  </div>
                </>
              )}

              {message && !isAccountSuspended && (
                <div className="text-red-500 text-sm text-center py-2">
                  {message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || isAccountSuspended}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {loginStep === 1 ? 'Sending OTP...' : 'Verifying...'}
                  </span>
                ) : loginStep === 1 ? 'Send OTP' : 'Verify OTP'}
              </button>
            </div>
          </form>

          {loginStep === 1 && (
            <div className="mt-6 text-center">
              <p className="text-gray-600 text-sm">
                Don't have an account?{' '}
                <Link to="/register" className="text-blue-600 font-medium hover:underline">
                  Register
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;

// import React, { useState, useEffect } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     phone: '',
//     otp: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');
//   const [loginStep, setLoginStep] = useState(1); // 1 = initial, 2 = OTP verification
//   const [resendCooldown, setResendCooldown] = useState(0);
//   const [isAccountSuspended, setIsAccountSuspended] = useState(false);

//   // Debug environment variables
//   console.log('Current Environment:', process.env.NODE_ENV);
//   console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

//   // Countdown timer for resend OTP
//   useEffect(() => {
//     let timer;
//     if (resendCooldown > 0) {
//       timer = setInterval(() => {
//         setResendCooldown(prev => prev - 1);
//       }, 1000);
//     }
//     return () => clearInterval(timer);
//   }, [resendCooldown]);

//   const validateForm = () => {
//     const { phone, otp } = formData;

//     if (loginStep === 1) {
//       if (!phone) {
//         return 'Phone number is required.';
//       }

//       const phoneRegex = /^[0-9]{10}$/;
//       if (!phoneRegex.test(phone)) {
//         return 'Enter a valid 10-digit phone number.';
//       }
//     }

//     if (loginStep === 2 && !otp) {
//       return 'OTP is required.';
//     }

//     return '';
//   };

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const sendLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');
//       setIsAccountSuspended(false);

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
//         { phone: formData.phone },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('OTP sent:', response);
//       toast.success('OTP sent to your phone!');
//       setLoginStep(2);
//       setResendCooldown(120); // 2 minutes cooldown
//     } catch (error) {
//       console.error('OTP send error:', error);
      
//       // Handle account suspension error
//       if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
//         setIsAccountSuspended(true);
//         setMessage('Your account has been suspended. Please contact support.');
//         toast.error('Your account has been suspended. Please contact support.');
//         return;
//       }
      
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Failed to send OTP. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const resendOtp = async () => {
//     if (resendCooldown > 0) return;
    
//     try {
//       setLoading(true);
//       setMessage('');
//       setIsAccountSuspended(false);

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
//         { phone: formData.phone },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('OTP resent:', response);
//       toast.success('OTP resent to your phone!');
//       setResendCooldown(120); // Reset cooldown to 2 minutes
//     } catch (error) {
//       console.error('OTP resend error:', error);
      
//       // Handle account suspension error
//       if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
//         setIsAccountSuspended(true);
//         setMessage('Your account has been suspended. Please contact support.');
//         toast.error('Your account has been suspended. Please contact support.');
//         return;
//       }
      
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Failed to resend OTP. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const verifyLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');
//       setIsAccountSuspended(false);

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/verifyLoginOtpAndLogin`,
//         { phone: formData.phone, otp: formData.otp },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('Login response:', response);

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: process.env.NODE_ENV === 'production',
//         sameSite: 'Lax'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       console.error('Login error:', error);
      
//       // Handle account suspension error
//       if (error.response?.data?.msg === 'Your account has been suspended, please wait until it is active again.') {
//         setIsAccountSuspended(true);
//         setMessage('Your account has been suspended. Please contact support.');
//         toast.error('Your account has been suspended. Please contact support.');
//         return;
//       }
      
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Login failed. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }
    
//     if (loginStep === 1) {
//       await sendLoginOtp();
//     } else {
//       await verifyLoginOtp();
//     }
//   };

//   const handleBack = () => {
//     setLoginStep(1);
//     setFormData(prev => ({ ...prev, otp: '' }));
//     setMessage('');
//     setIsAccountSuspended(false);
//     setResendCooldown(0);
//   };

//   // Format time for display (MM:SS)
//   const formatTime = (seconds) => {
//     const mins = Math.floor(seconds / 60);
//     const secs = seconds % 60;
//     return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
//   };

//   return (
//     <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
//         <div className="p-6">
//           <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
//             {loginStep === 1 ? 'Login with Phone Number' : 'Verify OTP'}
//           </h2>

//           {isAccountSuspended && (
//             <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
//               <p className="font-bold">Account Suspended</p>
//               <p>Your account has been suspended. Please contact support for assistance.</p>
//             </div>
//           )}

//           <form onSubmit={handleSubmit}>
//             <div className="space-y-4">
//               {loginStep === 1 ? (
//                 <div>
//                   <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
//                     Phone Number
//                   </label>
//                   <input
//                     type="text"
//                     name="phone"
//                     id="phone"
//                     value={formData.phone}
//                     onChange={handleChange}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                     placeholder="Enter 10-digit phone number"
//                     disabled={isAccountSuspended}
//                   />
//                 </div>
//               ) : (
//                 <>
//                   <div className="text-center mb-4">
//                     <p className="text-gray-600">
//                       Enter OTP sent to <span className="font-medium">******{formData.phone.slice(-3)}</span>
//                     </p>
//                   </div>
//                   <div>
//                     <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
//                       OTP Code
//                     </label>
//                     <input
//                       type="text"
//                       name="otp"
//                       id="otp"
//                       value={formData.otp}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
//                       placeholder="123456"
//                       maxLength={6}
//                       disabled={isAccountSuspended}
//                     />
//                   </div>
//                   <div className="text-center">
//                     <button
//                       type="button"
//                       onClick={handleBack}
//                       className="text-sm text-blue-600 font-medium hover:underline"
//                     >
//                       Back to login
//                     </button>
//                   </div>
                  
//                   {/* Resend OTP section */}
//                   <div className="text-center mt-4">
//                     <p className="text-gray-600 text-sm">
//                       Didn't receive the code?{' '}
//                       {resendCooldown > 0 ? (
//                         <span className="text-gray-500">
//                           Resend in {formatTime(resendCooldown)}
//                         </span>
//                       ) : (
//                         <button
//                           type="button"
//                           onClick={resendOtp}
//                           className="text-blue-600 font-medium hover:underline"
//                           disabled={loading || isAccountSuspended}
//                         >
//                           Resend OTP
//                         </button>
//                       )}
//                     </p>
//                   </div>
//                 </>
//               )}

//               {message && !isAccountSuspended && (
//                 <div className="text-red-500 text-sm text-center py-2">
//                   {message}
//                 </div>
//               )}

//               <button
//                 type="submit"
//                 disabled={loading || isAccountSuspended}
//                 className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
//               >
//                 {loading ? (
//                   <span className="flex items-center justify-center">
//                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     {loginStep === 1 ? 'Sending OTP...' : 'Verifying...'}
//                   </span>
//                 ) : loginStep === 1 ? 'Send OTP' : 'Verify OTP'}
//               </button>
//             </div>
//           </form>

//           {loginStep === 1 && (
//             <div className="mt-6 text-center">
//               <p className="text-gray-600 text-sm">
//                 Don't have an account?{' '}
//                 <Link to="/register" className="text-blue-600 font-medium hover:underline">
//                   Register
//                 </Link>
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;

// import React, { useState } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     phone: '',
//     otp: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');
//   const [loginStep, setLoginStep] = useState(1); // 1 = initial, 2 = OTP verification

//   // Debug environment variables
//   console.log('Current Environment:', process.env.NODE_ENV);
//   console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

//   const validateForm = () => {
//     const { phone, otp } = formData;

//     if (loginStep === 1) {
//       if (!phone) {
//         return 'Phone number is required.';
//       }

//       const phoneRegex = /^[0-9]{10}$/;
//       if (!phoneRegex.test(phone)) {
//         return 'Enter a valid 10-digit phone number.';
//       }
//     }

//     if (loginStep === 2 && !otp) {
//       return 'OTP is required.';
//     }

//     return '';
//   };

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const sendLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
//         { phone: formData.phone },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('OTP sent:', response);
//       toast.success('OTP sent to your phone!');
//       setLoginStep(2);
//     } catch (error) {
//       console.error('OTP send error:', error);
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Failed to send OTP. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const verifyLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/verifyLoginOtpAndLogin`,
//         { phone: formData.phone, otp: formData.otp },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('Login response:', response);

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: process.env.NODE_ENV === 'production',
//         sameSite: 'Lax'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       console.error('Login error:', error);
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Login failed. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }
    
//     if (loginStep === 1) {
//       await sendLoginOtp();
//     } else {
//       await verifyLoginOtp();
//     }
//   };

//   const handleBack = () => {
//     setLoginStep(1);
//     setFormData(prev => ({ ...prev, otp: '' }));
//     setMessage('');
//   };

//   return (
//     <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
//         <div className="p-6">
//           <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
//             {loginStep === 1 ? 'Login with Phone Number' : 'Verify OTP'}
//           </h2>

//           <form onSubmit={handleSubmit}>
//             <div className="space-y-4">
//               {loginStep === 1 ? (
//                 <div>
//                   <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
//                     Phone Number
//                   </label>
//                   <input
//                     type="text"
//                     name="phone"
//                     id="phone"
//                     value={formData.phone}
//                     onChange={handleChange}
//                     className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                     placeholder="Enter 10-digit phone number"
//                   />
//                 </div>
//               ) : (
//                 <>
//                   <div className="text-center mb-4">
//                     <p className="text-gray-600">
//                       Enter OTP sent to <span className="font-medium">******{formData.phone.slice(-3)}</span>
//                     </p>
//                   </div>
//                   <div>
//                     <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
//                       OTP Code
//                     </label>
//                     <input
//                       type="text"
//                       name="otp"
//                       id="otp"
//                       value={formData.otp}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
//                       placeholder="123456"
//                       maxLength={6}
//                     />
//                   </div>
//                   <div className="text-center">
//                     <button
//                       type="button"
//                       onClick={handleBack}
//                       className="text-sm text-blue-600 font-medium hover:underline"
//                     >
//                       Back to login
//                     </button>
//                   </div>
//                 </>
//               )}

//               {message && (
//                 <div className="text-red-500 text-sm text-center py-2">
//                   {message}
//                 </div>
//               )}

//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
//               >
//                 {loading ? (
//                   <span className="flex items-center justify-center">
//                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     {loginStep === 1 ? 'Sending OTP...' : 'Verifying...'}
//                   </span>
//                 ) : loginStep === 1 ? 'Send OTP' : 'Verify OTP'}
//               </button>
//             </div>
//           </form>

//           {loginStep === 1 && (
//             <div className="mt-6 text-center">
//               <p className="text-gray-600 text-sm">
//                 Don't have an account?{' '}
//                 <Link to="/register" className="text-blue-600 font-medium hover:underline">
//                   Register
//                 </Link>
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;

// import React, { useState } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     identifier: '',
//     password: '',
//     otp: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');
//   const [loginStep, setLoginStep] = useState(1); // 1 = initial, 2 = OTP verification
//   const [isPhoneLogin, setIsPhoneLogin] = useState(false);

//   // Debug environment variables
//   console.log('Current Environment:', process.env.NODE_ENV);
//   console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

//   const validateForm = () => {
//     const { identifier, password } = formData;

//     if (!identifier) {
//       return 'Email or phone number is required.';
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     const phoneRegex = /^[0-9]{10}$/;

//     const isEmail = emailRegex.test(identifier);
//     const isPhone = phoneRegex.test(identifier);

//     if (!isEmail && !isPhone) {
//       return 'Enter a valid email or 10-digit phone number.';
//     }

//     // For phone logins, password is only required in first step
//     if (loginStep === 1 && !password) {
//       return 'Password is required.';
//     }

//     if (isPhone && loginStep === 2 && !formData.otp) {
//       return 'OTP is required.';
//     }

//     return '';
//   };

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const sendLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/sendLoginOtp`,
//         { phone: formData.identifier, password: formData.password },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('OTP sent:', response);
//       toast.success('OTP sent to your phone!');
//       setLoginStep(2);
//       setIsPhoneLogin(true);
//     } catch (error) {
//       console.error('OTP send error:', error);
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Failed to send OTP. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const verifyLoginOtp = async () => {
//     try {
//       setLoading(true);
//       setMessage('');

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/verifyLoginOtpAndLogin`,
//         { phone: formData.identifier, otp: formData.otp },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('Login response:', response);

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: process.env.NODE_ENV === 'production',
//         sameSite: 'Lax'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       console.error('Login error:', error);
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Login failed. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleEmailLogin = async () => {
//     try {
//       setLoading(true);
//       setMessage('');

//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/login`,
//         { email: formData.identifier, password: formData.password },
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('Login response:', response);

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: process.env.NODE_ENV === 'production',
//         sameSite: 'Lax'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       console.error('Login error:', error);
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Login failed. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }

//     const isPhone = /^[0-9]{10}$/.test(formData.identifier);
    
//     if (isPhone && loginStep === 1) {
//       await sendLoginOtp();
//     } 
//     else if (isPhone && loginStep === 2) {
//       await verifyLoginOtp();
//     } 
//     else {
//       await handleEmailLogin();
//     }
//   };

//   const handleBack = () => {
//     setLoginStep(1);
//     setIsPhoneLogin(false);
//     setFormData(prev => ({ ...prev, otp: '' }));
//     setMessage('');
//   };

//   return (
//     <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
//         <div className="p-6">
//           <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
//             {loginStep === 1 ? 'Login to Your Account' : 'Verify OTP'}
//           </h2>

//           <form onSubmit={handleSubmit}>
//             <div className="space-y-4">
//               {loginStep === 1 ? (
//                 <>
//                   <div>
//                     <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
//                       Phone Number
//                     </label>
//                     <input
//                       type="text"
//                       name="identifier"
//                       id="identifier"
//                       value={formData.identifier}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                       placeholder="Enter phone number"
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
//                       Password
//                     </label>
//                     <input
//                       type="password"
//                       name="password"
//                       id="password"
//                       value={formData.password}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                       placeholder="Enter your password"
//                     />
//                   </div>
//                 </>
//               ) : (
//                 <>
//                   <div className="text-center mb-4">
//                     <p className="text-gray-600">
//                       Enter OTP sent to <span className="font-medium">******{formData.identifier.slice(-3)}</span>
//                     </p>
//                   </div>
//                   <div>
//                     <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
//                       OTP Code
//                     </label>
//                     <input
//                       type="text"
//                       name="otp"
//                       id="otp"
//                       value={formData.otp}
//                       onChange={handleChange}
//                       className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg"
//                       placeholder="123456"
//                       maxLength={6}
//                     />
//                   </div>
//                   <div className="text-center">
//                     <button
//                       type="button"
//                       onClick={handleBack}
//                       className="text-sm text-blue-600 font-medium hover:underline"
//                     >
//                       Back to login
//                     </button>
//                   </div>
//                 </>
//               )}

//               {message && (
//                 <div className="text-red-500 text-sm text-center py-2">
//                   {message}
//                 </div>
//               )}

//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
//               >
//                 {loading ? (
//                   <span className="flex items-center justify-center">
//                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     {loginStep === 1 ? 'Processing...' : 'Verifying...'}
//                   </span>
//                 ) : loginStep === 1 ? 'Log In' : 'Verify OTP'}
//               </button>
//             </div>
//           </form>

//           {loginStep === 1 && (
//             <div className="mt-6 text-center">
//               <p className="text-gray-600 text-sm">
//                 Don't have an account?{' '}
//                 <Link to="/register" className="text-blue-600 font-medium hover:underline">
//                   Register
//                 </Link>
//               </p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;

// import React, { useState } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     identifier: '',
//     password: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');

//   // Debug environment variables
//   console.log('Current Environment:', process.env.NODE_ENV);
//   console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);

//   const validateForm = () => {
//     const { identifier, password } = formData;

//     if (!identifier || !password) {
//       return 'All fields are required.';
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     const phoneRegex = /^[0-9]{10}$/;

//     const isEmail = emailRegex.test(identifier);
//     const isPhone = phoneRegex.test(identifier);

//     if (!isEmail && !isPhone) {
//       return 'Enter a valid email or 10-digit phone number.';
//     }

//     if (password.length < 6) {
//       return 'Password must be at least 6 characters.';
//     }

//     return '';
//   };

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setMessage('');

//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }

//     setLoading(true);

//     const requestBody = { password: formData.password };
//     if (formData.identifier.includes('@')) {
//       requestBody.email = formData.identifier;
//     } else {
//       requestBody.phone = formData.identifier;
//     }

//     try {
//       console.log('Attempting login with:', requestBody); // Debug log
      
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/login`,
//         requestBody,
//         {
//           headers: {
//             'Content-Type': 'application/json',
//             'Accept': 'application/json'
//           },
//           withCredentials: true
//         }
//       );

//       console.log('Login response:', response); // Debug log

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: process.env.NODE_ENV === 'production',
//         sameSite: 'Lax'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       console.error('Login error:', error);
//       console.error('Error response:', error.response);
      
//       const errorMessage = error.response?.data?.message || 
//                          error.message || 
//                          'Login failed. Please try again.';
//       setMessage(errorMessage);
//       toast.error(errorMessage);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
//         <div className="p-6">
//           <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
//             Login to Your Account
//           </h2>

//           <form onSubmit={handleSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
//                   Email or Phone Number
//                 </label>
//                 <input
//                   type="text"
//                   name="identifier"
//                   id="identifier"
//                   value={formData.identifier}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter email or phone"
//                 />
//               </div>

//               <div>
//                 <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
//                   Password
//                 </label>
//                 <input
//                   type="password"
//                   name="password"
//                   id="password"
//                   value={formData.password}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter your password"
//                 />
//               </div>

//               {message && (
//                 <div className="text-red-500 text-sm text-center py-2">
//                   {message}
//                 </div>
//               )}

//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
//               >
//                 {loading ? (
//                   <span className="flex items-center justify-center">
//                     <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Processing...
//                   </span>
//                 ) : 'Log In'}
//               </button>
//             </div>
//           </form>

//           <div className="mt-6 text-center">
//             <p className="text-gray-600 text-sm">
//               Don't have an account?{' '}
//               <Link to="/register" className="text-blue-600 font-medium hover:underline">
//                 Register
//               </Link>
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;

// import React, { useState } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';
// import { toast } from 'react-toastify';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     identifier: '',
//     password: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');

//   const validateForm = () => {
//     const { identifier, password } = formData;

//     if (!identifier || !password) {
//       return 'All fields are required.';
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     const phoneRegex = /^[0-9]{10}$/;

//     const isEmail = emailRegex.test(identifier);
//     const isPhone = phoneRegex.test(identifier);

//     if (!isEmail && !isPhone) {
//       return 'Enter a valid email or 10-digit phone number.';
//     }

//     if (password.length < 6) {
//       return 'Password must be at least 6 characters.';
//     }

//     return '';
//   };

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setMessage('');

//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }

//     setLoading(true);

//     const requestBody = { password: formData.password };
//     if (formData.identifier.includes('@')) {
//       requestBody.email = formData.identifier;
//     } else {
//       requestBody.phone = formData.identifier;
//     }

//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/login`,
//         requestBody
//       );

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: true,
//         sameSite: 'Strict'
//       });

//       toast.success('Login successful!');
//       navigate('/home');
//     } catch (error) {
//       setMessage(error?.response?.data?.message || 'Login failed.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="flex justify-center items-center min-h-screen bg-gray-100 p-4">
//       <div className="w-full max-w-md bg-white rounded-xl shadow-lg overflow-hidden">
//         <div className="p-6">
//           <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
//             Login to Your Account
//           </h2>

//           <form onSubmit={handleSubmit}>
//             <div className="space-y-4">
//               <div>
//                 <label htmlFor="identifier" className="block text-sm font-medium text-gray-700 mb-1">
//                   Email or Phone Number
//                 </label>
//                 <input
//                   type="text"
//                   name="identifier"
//                   id="identifier"
//                   value={formData.identifier}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter email or phone"
//                 />
//               </div>

//               <div>
//                 <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
//                   Password
//                 </label>
//                 <input
//                   type="password"
//                   name="password"
//                   id="password"
//                   value={formData.password}
//                   onChange={handleChange}
//                   className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//                   placeholder="Enter your password"
//                 />
//               </div>

//               {/* <div className="flex justify-end">
//                 <Link 
//                   to="/forgot-password" 
//                   className="text-sm text-blue-600 hover:underline"
//                 >
//                   Forgot password?
//                 </Link>
//               </div> */}

//               {message && (
//                 <div className="text-red-500 text-sm text-center py-2">
//                   {message}
//                 </div>
//               )}

//               <button
//                 type="submit"
//                 disabled={loading}
//                 className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition duration-300 disabled:opacity-50"
//               >
//                 {loading ? 'Logging in...' : 'Log In'}
//               </button>
//             </div>
//           </form>

//           <div className="mt-6 text-center">
//             <p className="text-gray-600 text-sm">
//               Don't have an account?{' '}
//               <Link to="/register" className="text-blue-600 font-medium hover:underline">
//                 Register
//               </Link>
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;

// import React, { useState } from 'react';
// import { Link, useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from 'js-cookie';

// const Login = () => {
//   const navigate = useNavigate();
//   const dispatch = useDispatch();

//   const [formData, setFormData] = useState({
//     identifier: '',
//     password: ''
//   });

//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');

//   const handleChange = (e) => {
//     setFormData((prev) => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const validateForm = () => {
//     const { identifier, password } = formData;

//     if (!identifier || !password) {
//       return 'All fields are required.';
//     }

//     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
//     const phoneRegex = /^[0-9]{10}$/;

//     const isEmail = emailRegex.test(identifier);
//     const isPhone = phoneRegex.test(identifier);

//     if (!isEmail && !isPhone) {
//       return 'Enter a valid email or 10-digit phone number.';
//     }

//     if (password.length < 6) {
//       return 'Password must be at least 6 characters.';
//     }

//     return '';
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setMessage('');

//     const validationError = validateForm();
//     if (validationError) {
//       setMessage(validationError);
//       return;
//     }

//     setLoading(true);

//     const requestBody = { password: formData.password };
//     if (formData.identifier.includes('@')) {
//       requestBody.email = formData.identifier;
//     } else {
//       requestBody.phone = formData.identifier;
//     }

//     try {
//       const response = await axios.post(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/login`,
//         requestBody
//       );

//       dispatch(
//         loginSuccess({
//           user: response?.data?.user,
//           token: response?.data?.token
//         })
//       );

//       Cookies.set('ecom_user_token', response?.data?.token, {
//         expires: 1,
//         secure: true,
//         sameSite: 'Strict'
//       });

//       navigate('/home');
//     } catch (error) {
//       setMessage(error?.response?.data?.message || 'Login failed.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="flex justify-center items-center h-screen bg-gray-200">
//       <form
//         className="flex flex-col w-72 p-5 rounded bg-white shadow-md"
//         onSubmit={handleSubmit}
//       >
//         <h2 className="text-xl font-semibold text-center mb-4">Login</h2>

//         <div className="mb-4">
//           <label htmlFor="identifier" className="block text-gray-700">
//             Email ID / Phone Number
//           </label>
//           <input
//             type="text"
//             name="identifier"
//             id="identifier"
//             value={formData.identifier}
//             onChange={handleChange}
//             className="w-full p-2 border rounded-md"
//             required
//           />
//         </div>

//         <div className="mb-4">
//           <label htmlFor="password" className="block text-gray-700">
//             Password
//           </label>
//           <input
//             type="password"
//             name="password"
//             id="password"
//             value={formData.password}
//             onChange={handleChange}
//             className="w-full p-2 border rounded-md"
//             required
//           />
//         </div>

//         <button
//           type="submit"
//           disabled={loading}
//           className="p-2.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition"
//         >
//           {loading ? 'Logging in...' : 'Log In'}
//         </button>

//         {message && (
//           <p className="text-center mt-3 text-sm text-red-500">{message}</p>
//         )}

//         <div className="mt-4 text-center">
//           <Link to="/register" className="text-blue-500 hover:underline">
//             Create an account
//           </Link>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default Login;


// import React, { useState } from 'react';
// import { Link,useNavigate} from 'react-router-dom';
// import axios from 'axios';
// import { useDispatch } from 'react-redux';
// import { loginSuccess } from '../redux/slices/authSlice';
// import Cookies from "js-cookie";

// const Login = () => {
//   const navigate = useNavigate()
//   const [formData, setFormData] = useState({
//     identifier: '',
//     password: ''
//   });
//   const dispatch = useDispatch();
//   const [loading, setLoading] = useState(false);
//   const [message, setMessage] = useState('');

//   const handleChange = (e) => {
//     setFormData(prev => ({
//       ...prev,
//       [e.target.name]: e.target.value
//     }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setMessage('');

//     // Prepare request body: detect whether it's an email or phone
//     const requestBody = {
//       password: formData.password
//     };

//     if (formData.identifier.includes('@')) {
//       requestBody.email = formData.identifier;
//     } else {
//       requestBody.phone = formData.identifier;
//     }

//     try {
//       const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/buyer/login`, requestBody);
//       console.log('Login successful:', response.data);
//       setMessage('Login successful!');
//       dispatch(loginSuccess({ user: response?.data?.user, token: response?.data?.token }));
//       Cookies.set("ecom_user_token", response?.data?.token, {
//           expires: 1,
//           secure: true,
//           sameSite: "Strict",
//         });

//       navigate('/home')
//       // You can store token or redirect here
//     } catch (error) {
//       setMessage(error?.response?.data?.message || 'Login failed.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <div className="flex justify-center items-center h-screen bg-grey-500">
//       <form className="flex flex-col w-72 p-5 rounded bg-white shadow-md" onSubmit={handleSubmit}>
//         <h2 className="text-xl font-semibold text-center mb-4">Login</h2>

//         <div className="mb-4">
//           <label htmlFor="identifier" className="block text-gray-700">Email ID / Phone Number</label>
//           <input
//             type="text"
//             name="identifier"
//             id="identifier"
//             value={formData.identifier}
//             onChange={handleChange}
//             className="w-full p-2 border rounded-md"
//             required
//           />
//         </div>

//         <div className="mb-4">
//           <label htmlFor="password" className="block text-gray-700">Password</label>
//           <input
//             type="password"
//             name="password"
//             id="password"
//             value={formData.password}
//             onChange={handleChange}
//             className="w-full p-2 border rounded-md"
//             required
//           />
//         </div>

//         <button type="submit" disabled={loading}
//           className="p-2.5 rounded bg-blue-600 text-white hover:bg-blue-700 transition">
//           {loading ? 'Logging in...' : 'Log In'}
//         </button>

//         {message && <p className="text-center mt-3 text-sm text-red-500">{message}</p>}

//         <div className="mt-4 text-center">
//           <Link to="/register" className="text-blue-500 hover:underline">Create an account</Link>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default Login;


// import React, { useState } from 'react';
// import { Link } from 'react-router-dom';

// const Login = () => {
//   const [inputValue, setInputValue] = useState('');

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     console.log(inputValue);
//   };

//   return (
//     <div className="flex justify-center items-center h-screen bg-grey-500">
//       <form className="flex flex-col w-72 p-5 rounded bg-white shadow-md" onSubmit={handleSubmit}>
//       <div className="mb-4">
//             <label htmlFor="email" className="block text-gray-700">Email ID / Phone Number</label>
//             <input type="text" id="email" className="w-full p-2 border rounded-md" required />
//           </div>
//           <div className="mb-4">
//             <label htmlFor="password" className="block text-gray-700">Password</label>
//             <input type="password" id="password" className="w-full p-2 border rounded-md" required />
//           </div>
//         <button type="login"className="p-2.5 rounded bg-blue-600 text-white cursor-pointer">
//           Log In
//         </button>
//         <div className="mt-4 text-center">
//           <Link to="/register" className="text-blue-500 hover:underline">Create an account</Link>
//         </div>
//       </form>
//     </div>
//   );
// };

// export default Login;
